
import { logger } from './logger';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Shield, Settings, Menu, Bell, Wifi, Activity, Zap, Info, Speaker, Target, PanelRightOpen, Download, AlertTriangle, RotateCcw, LogIn, LogOut, MessageSquare, ShieldAlert, Eye, X, Terminal, Dog, Gamepad2, Newspaper, PenSquare, User as UserIcon } from 'lucide-react';
import TacticalMap, { EventPin, SponsoredPin } from './components/MapboxMap';
import IncidentLog from './components/IncidentLog';
import ProfileDialog from './components/ProfileDialog';
import AlarmSettingsPanel from './components/AlarmSettingsPanel';
import SubscriptionModal from './components/SubscriptionModal';
import SafetyCompanion from './components/SafetyCompanion';
import { playAlertTone, playDispatchBeep } from './services/scannerAudio';
import { useSubscription } from './hooks/useSubscription';
import { useSSE } from './hooks/useSSE';
import DangerAlert from './components/DangerAlert';
import BusinessBillboard from './components/BusinessBillboard';
import DevTools from './components/DevTools';
import AgeGateAndToS from './components/AgeGateAndToS';
import ChatRoom from './components/ChatRoom';
import GamePanel from './components/GamePanel';
import RadioFeed from './components/RadioFeed';
import RadioPlayer from './components/RadioPlayer';
import TicketModal from './components/TicketModal';
import Feed from './components/Feed';
import PostComposer from './components/PostComposer';
import TrustedSourceApplyModal from './components/TrustedSourceApplyModal';
import NotificationSettings from './components/NotificationSettings';
import AdSlot from './components/AdSlot';
import { postsApi, Post, PostMapMarker, PostCategory } from './services/postsApi';
import { notificationsApi } from './services/notifications';
import { GeoLocation, Incident, ThreatLevel } from './types';
import { fetchLiveThreats, playTacticalAlert, analyzeIncidentVicinity, summarizeDispatch, geocodeAddress, generateWildWestConcept } from './services/geminiService';
import { saveIncidents, loadIncidents } from './services/database';
import Papa from 'papaparse';
import { auth, db, signInWithGoogle, handleRedirectResult, logOut } from './firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import { doc, setDoc, getDoc, collection, onSnapshot, query, orderBy, limit } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId: string | undefined;
    email: string | null | undefined;
    emailVerified: boolean | undefined;
    isAnonymous: boolean | undefined;
    tenantId: string | null | undefined;
    providerInfo: {
      providerId: string;
      displayName: string | null;
      email: string | null;
      photoUrl: string | null;
    }[];
  }
}

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData.map(provider => ({
        providerId: provider.providerId,
        displayName: provider.displayName,
        email: provider.email,
        photoUrl: provider.photoURL
      })) || []
    },
    operationType,
    path
  }
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

const DEFAULT_SYNC_INTERVAL = 240000; // 4m
const MAX_INCIDENTS_TRACKED = 20;
const MAX_DATA_AGE_MINUTES = 2880; // 48h // 24h — match the scanner feed window

const isIncidentRecent = (incident: Incident, maxMinutes: number): boolean => {
  const maxAgeMillis = maxMinutes * 60 * 1000;
  const incidentAgeMillis = Date.now() - incident.timestamp;
  return incidentAgeMillis <= maxAgeMillis;
};


export default function App() {
  const [incidents, setIncidents] = useState<Incident[]>(() => {
    const cachedIncidents = loadIncidents();
    // Drop community-post-derived entries from cache — those are re-fetched fresh from /api/posts.
    return cachedIncidents.filter(inc =>
      isIncidentRecent(inc, MAX_DATA_AGE_MINUTES) && !String(inc.id).startsWith('post:'),
    );
  });
  const [isFeedOpen, setIsFeedOpen]         = useState(false);
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [isTrustedApplyOpen, setIsTrustedApplyOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotifSettingsOpen, setIsNotifSettingsOpen] = useState(false);
  const [userLocation, setUserLocation] = useState<GeoLocation | null>(() => {
    const saved = localStorage.getItem('userLocation');
    return saved ? JSON.parse(saved) : null;
  });
  const [userGeminiKey, setUserGeminiKey] = useState<string>(() => localStorage.getItem('userGeminiKey') || '');
  const [isSubModalOpen, setIsSubModalOpen] = useState(false);
  const [isSponsorAdModalOpen, setIsSponsorAdModalOpen] = useState(false);
  const [isSafetyOpen, setIsSafetyOpen] = useState(false);
  const [aryaLocation, setAryaLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [venueTarget, setVenueTarget] = useState<{ lat: number; lng: number; name: string } | null>(null);
  const [eventPins, setEventPins] = useState<EventPin[]>([]);
  const [sponsoredPins, setSponsoredPins] = useState<SponsoredPin[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<EventPin | null>(null);
  const [dangerRadius, setDangerRadius] = useState(() => {
    const saved = localStorage.getItem('dangerRadius');
    return saved ? JSON.parse(saved) : 0.25;
  });
  
  useEffect(() => {
    localStorage.setItem('dangerRadius', JSON.stringify(dangerRadius));
  }, [dangerRadius]);

  // Fetch event pins + sponsored pins on mount, refresh every 5 min
  useEffect(() => {
    const load = async () => {
      try {
        const [evRes, spRes] = await Promise.all([
          fetch('/api/pulse/map'),
          fetch('/api/sponsors/active'),
        ]);
        if (evRes.ok) setEventPins((await evRes.json()).events || []);
        if (spRes.ok) setSponsoredPins((await spRes.json()).pins || []);
      } catch (e) {
        console.warn('[pins]', e);
      }
    };
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  // ── Community posts → map markers (USER_REPORT incidents) ──
  // We merge community posts into the existing `incidents` state with stable
  // `post:<id>` keys so the map renders them alongside CAD/scanner data using
  // the same layer pipeline. Visual distinction is in MapboxMap's paint expr.
  //
  // Realtime updates come through the shared sseHub (see services/sseHub.ts)
  // via useSSE — App and the Feed component now share one EventSource.
  useEffect(() => {
    if (!userLocation) return;
    let alive = true;

    const postCategoryToThreat = (cat: PostCategory): ThreatLevel => {
      switch (cat) {
        case 'shooting': case 'pursuit': case 'containment': return ThreatLevel.CRITICAL;
        case 'police_activity': case 'suspicious':           return ThreatLevel.HIGH;
        case 'fire_ems': case 'traffic':                     return ThreatLevel.MEDIUM;
        default:                                             return ThreatLevel.LOW;
      }
    };
    const markerToIncident = (m: PostMapMarker): Incident => ({
      id: `post:${m.id}`,
      type: m.title.slice(0, 40),
      description: m.title,
      location: { lat: m.lat, lng: m.lon },
      timestamp: m.created_at * 1000,
      threatLevel: postCategoryToThreat(m.category),
      source: 'USER_REPORT',
      isConfirmed: m.upvotes > 0,
    });

    const initialFetch = async () => {
      try {
        const r = await postsApi.map(userLocation.lat, userLocation.lng, 10);
        if (!alive) return;
        const incs = r.markers.map(markerToIncident);
        setIncidents(prev => {
          const without = prev.filter(p => !String(p.id).startsWith('post:'));
          return [...without, ...incs];
        });
      } catch (e) { console.warn('[posts/map]', e); }
    };
    initialFetch();
const refresh = window.setInterval(initialFetch, 5 * 60 * 1000);
    return () => {
      alive = false;
      window.clearInterval(refresh);
    };
  }, [userLocation?.lat, userLocation?.lng]);

  // ── Automatic Live CAD Dispatch Hydration on Load (Pushes data directly to right panel) ──
  useEffect(() => {
    let alive = true;
    const fetchLiveCadCalls = async () => {
      try {
        const loc = userLocation ?? { lat: 32.2226, lng: -110.9747 };
        let res = await fetch(`/api/calls/live/feed?lat=${loc.lat}&lon=${loc.lng}&radius=15&limit=50&hours=24`);
        let data = res.ok ? await res.json() : null;
        let feedItems = (data && data.feed && Array.isArray(data.feed) && data.feed.length > 0) ? data.feed : null;

        // Fallback to /api/calls/live/all if feed returns fewer than 5 calls
        if (!feedItems || feedItems.length < 5) {
          const allRes = await fetch(`/api/calls/live/all?hours=24`);
          if (allRes.ok) {
            const allData = await allRes.json();
            if (allData.calls && Array.isArray(allData.calls)) {
              feedItems = allData.calls.slice(0, 50).map((c: any) => ({
                ...c,
                text: `${c.level === '1' ? 'EMERGENCY' : c.level === '2' ? 'PRIORITY' : 'DISPATCH'} — ${c.type || 'CALL FOR SERVICE'} AT ${c.address || 'TUCSON'}`
              }));
            }
          }
        }

        console.log('[live-cad-fetch] received', feedItems ? feedItems.length : 0, 'items');
        if (feedItems && feedItems.length > 0 && alive) {
          const cadIncidents: Incident[] = feedItems.map((c: any) => {
            let threat = ThreatLevel.LOW;
            if (c.level === '1') threat = ThreatLevel.CRITICAL;
            else if (c.level === '2') threat = ThreatLevel.HIGH;
            else if (c.level === '3') threat = ThreatLevel.MEDIUM;
            const t = (c.type || '').toUpperCase();
            if (/HOMICIDE|SHOOTING|STABBING|ROBBERY|ASSAULT|WEAPON/.test(t)) threat = ThreatLevel.HIGH;
            else if (/BURGLARY|THEFT|VEHICLE|DRUG|DUI/.test(t)) threat = ThreatLevel.MEDIUM;

            const R = 3959; // Earth radius in miles
            const dLat = (c.lat - loc.lat) * Math.PI / 180;
            const dLon = (c.lon - loc.lng) * Math.PI / 180;
            const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                      Math.cos(loc.lat * Math.PI / 180) * Math.cos(c.lat * Math.PI / 180) *
                      Math.sin(dLon/2) * Math.sin(dLon/2);
            const dist = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

            return {
              id: c.eid,
              type: c.type || 'CALL FOR SERVICE',
              description: c.text || `TPD Dispatch: ${c.type || 'Call for service'} at ${c.address || 'Tucson'}`,
              location: { lat: c.lat, lng: c.lon },
              timestamp: (c.ts || Math.floor(Date.now() / 1000)) * 1000,
              threatLevel: threat,
              source: 'DISPATCH' as const,
              distance: dist,
              isConfirmed: true,
            };
          });

          setIncidents(prev => {
            const existingIds = new Set(prev.map(p => p.id));
            const fresh = cadIncidents.filter(c => !existingIds.has(c.id));
            const merged = [...fresh, ...prev].slice(0, 150);
            saveIncidents(merged);
            return merged;
          });
        }
      } catch (e) {
        console.warn('[live-cad-fetch] error:', e);
      } finally {
        console.log('[live-cad-fetch] finished attempt');
      }
    };

    fetchLiveCadCalls();
    const interval = setInterval(fetchLiveCadCalls, 30000);
    return () => { alive = false; clearInterval(interval); };
  }, [userLocation?.lat, userLocation?.lng]);

  // Realtime SSE handlers — kept at component top level so the hub registers
  // a single subscription regardless of whether userLocation is set yet.
  useSSE<Post>('post.created', (post) => {
    if (!post || !post.location) return;
    const inc: Incident = {
      id: `post:${post.id}`,
      type: post.title.slice(0, 40),
      description: post.description.slice(0, 200),
      location: post.location,
      timestamp: post.createdAt * 1000,
      threatLevel: ThreatLevel[post.threatLevel as keyof typeof ThreatLevel] || ThreatLevel.LOW,
      source: 'USER_REPORT',
      isConfirmed: post.upvotes > 0,
    };
    setIncidents(prev => prev.some(p => p.id === inc.id) ? prev : [inc, ...prev]);
  });
  useSSE<{ id: number }>('post.deleted', ({ id }) => {
    if (id == null) return;
    setIncidents(prev => prev.filter(p => p.id !== `post:${id}`));
  });

  const [user, setUser] = useState<User | null>(null);
  const [myProfile, setMyProfile] = useState<import('./services/api').ProfileUser | null>(null);

  // Subscription hook — replaces isPaidSubscriber boolean
  const { tier, activate: activateTier, canUseOwnKey, canUseAI, canUseTTS, canUseDanger, canUseGameMode, isFree } = useSubscription(user?.uid ?? null);
  const [isScanning, setIsScanning] = useState(false);
  const [proximityAlarmEnabled, setProximityAlarmEnabled] = useState(() => {
    const saved = localStorage.getItem('proximityAlarmEnabled');
    return saved ? JSON.parse(saved) : (typeof window !== 'undefined' && window.innerWidth >= 1024);
  });
  const [statusText, setStatusText] = useState("LOADING CACHED INTEL...");
  const [authError, setAuthError] = useState<{code:string,message:string}|null>(null);
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  const [selectedBusiness, setSelectedBusiness] = useState<any | null>(null);
  const [vicinityReport, setVicinityReport] = useState<string>("");
  const [inDangerZone, setInDangerZone] = useState(false);
  const [closestDanger, setClosestDanger] = useState<Incident | null>(null);
  const [backoffActive, setBackoffActive] = useState(false);
  const [summarizingIncidentId, setSummarizingIncidentId] = useState<string | null>(null);
  const [highlightedIncidentId, setHighlightedIncidentId] = useState<string | null>(null);
  const [isLogVisible, setIsLogVisible] = useState(() => {
    const saved = localStorage.getItem('isLogVisible');
    return saved ? JSON.parse(saved) : false;
  });
  const [isLogDetached, setIsLogDetached] = useState(() => {
    const saved = localStorage.getItem('isLogDetached');
    return saved ? JSON.parse(saved) : false;
  });
  const [isLogGhost, setIsLogGhost] = useState(() => {
    const saved = localStorage.getItem('isLogGhost');
    return saved ? JSON.parse(saved) : false;
  });
  const [logGhostOpacity, setLogGhostOpacity] = useState(() => {
    const saved = localStorage.getItem('logGhostOpacity');
    return saved ? JSON.parse(saved) : 75;
  });
  const [logPosition, setLogPosition] = useState(() => {
    const saved = localStorage.getItem('logPosition');
    return saved ? JSON.parse(saved) : { x: window.innerWidth - 400, y: 80 };
  });
  const [logWidth, setLogWidth] = useState(() => {
    const saved = localStorage.getItem('logWidth');
    return saved ? JSON.parse(saved) : 350;
  });
  const [manualLocationMode, setManualLocationMode] = useState(false);
  const [locationInput, setLocationInput] = useState('');
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isLocationSet, setIsLocationSet] = useState(false);
  const [activeTab, setActiveTab] = useState<'threats' | 'alerts' | 'intel' | 'game' | 'pulse'>('threats');
  const [ghostProtocol, setGhostProtocol] = useState(false);
  const ghostClicksRef = React.useRef(0);
  const [versionClickCount, setVersionClickCount] = useState(0);
  const [isDevToolsOpen, setIsDevToolsOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isChatDetached, setIsChatDetached] = useState(false);
  const [isAlarmPanelOpen, setIsAlarmPanelOpen] = useState(false);
  const [hasAgreedToToS, setHasAgreedToToS] = useState(false);
  const [vicinityAnalysisCooldown, setVicinityAnalysisCooldown] = useState(0);
  const [conceptImage, setConceptImage] = useState<string | null>(null);
  const [isGeneratingConcept, setIsGeneratingConcept] = useState(false);
  const [isVisionModalOpen, setIsVisionModalOpen] = useState(false);
  const [visionPrompt, setVisionPrompt] = useState("");
  const [devSettings, setDevSettings] = useState(() => {
    const saved = localStorage.getItem('devSettings');
    return saved ? JSON.parse(saved) : {
      syncInterval: 240000,
      vicinityCooldown: 60000,
      summarizeCooldown: 60000,
      theme: {
        primary: '#d4a843',                  // Desert Noir amber/gold
        danger: '#ff2244',                   // Emergency red
        bg: '#040108',                       // Deep black
        header: 'rgba(4, 1, 8, 0.95)',       // Near-black header
        border: 'rgba(212, 168, 67, 0.3)',   // Amber border
        cyan: '#00e5ff',                     // Electric cyan accent
      }
    };
  });
  const isResizingRef = useRef(false);
  
  const scanTimerRef = useRef<number | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const debounceTimer = useRef<number | null>(null);
  const geoAttemptedRef = useRef(false);
  const lastAlertTimeRef = useRef<number>(0);
  const lastVicinityAnalysisRef = useRef<number>(0);
  const lastSummarizeRef = useRef<number>(0);

  useEffect(() => {
    if (vicinityAnalysisCooldown > 0) {
      const timer = setTimeout(() => setVicinityAnalysisCooldown(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [vicinityAnalysisCooldown]);

  // Handle Google redirect sign-in result on mount
  useEffect(() => {
    handleRedirectResult().catch(() => {});
  }, []);

  // Register service worker on mount for push notifications.
  useEffect(() => {
    if (notificationsApi.supported()) {
      navigator.serviceWorker.register('/app/sw.js', { scope: '/app/' })
        .catch(e => console.warn('[sw] register:', e?.message));
    }
  }, []);

  // Persist preferences
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        // Auto-grant Ghost tier for owner account
        if (currentUser.email === 'desertshibari@gmail.com') {
          const existingLocal = localStorage.getItem('ts_subscription');
          if (!existingLocal) {
            localStorage.setItem('ts_subscription', JSON.stringify({
              tier: 'ghost', subscriptionId: 'owner-permanent', activatedAt: Date.now(),
            }));
          }
        }
        // Sync user profile to Firestore
        try {
          await setDoc(doc(db, 'users', currentUser.uid), {
            uid: currentUser.uid,
            email: currentUser.email,
            displayName: currentUser.displayName,
            photoURL: currentUser.photoURL,
            lastLogin: Date.now()
          }, { merge: true });
        } catch (error) {
          handleFirestoreError(error, OperationType.WRITE, `users/${currentUser.uid}`);
        }

        // Mirror identity into the API's SQLite users table (Phase 0). Best-effort.
        try {
          const { usersApi } = await import('./services/api');
          await usersApi.sync();
          try {
            const me = await usersApi.me();
            setMyProfile(me.user);
          } catch (e) {
            console.warn('[users/me] failed:', e);
          }
        } catch (e) {
          console.warn('[users/sync] failed:', e);
        }

        // Listen for user-specific incidents
        const q = query(
          collection(db, 'users', currentUser.uid, 'incidents'),
          orderBy('timestamp', 'desc'),
          limit(50)
        );
        const unsubIncidents = onSnapshot(q, (snapshot) => {
          const cloudIncidents = snapshot.docs.map(doc => doc.data() as Incident);
          setIncidents(prev => {
            const merged = [...cloudIncidents, ...prev.filter(p => !cloudIncidents.some(c => c.id === p.id))];
            return merged.sort((a, b) => b.timestamp - a.timestamp).slice(0, 100);
          });
        }, (error) => {
          handleFirestoreError(error, OperationType.LIST, `users/${currentUser.uid}/incidents`);
        });
        return () => unsubIncidents();
      } else {
        setMyProfile(null);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    localStorage.setItem('proximityAlarmEnabled', JSON.stringify(proximityAlarmEnabled));
  }, [proximityAlarmEnabled]);

  useEffect(() => {
    localStorage.setItem('isLogVisible', JSON.stringify(isLogVisible));
  }, [isLogVisible]);

  useEffect(() => {
    localStorage.setItem('logWidth', JSON.stringify(logWidth));
  }, [logWidth]);

  useEffect(() => {
    localStorage.setItem('isLogDetached', JSON.stringify(isLogDetached));
  }, [isLogDetached]);

  useEffect(() => {
    localStorage.setItem('isLogGhost', JSON.stringify(isLogGhost));
  }, [isLogGhost]);

  useEffect(() => {
    localStorage.setItem('logGhostOpacity', JSON.stringify(logGhostOpacity));
  }, [logGhostOpacity]);

  useEffect(() => {
    localStorage.setItem('logPosition', JSON.stringify(logPosition));
  }, [logPosition]);

  useEffect(() => {
    if (userLocation) {
      localStorage.setItem('userLocation', JSON.stringify(userLocation));
    }
  }, [userLocation]);

  useEffect(() => {
    localStorage.setItem('devSettings', JSON.stringify(devSettings));
  }, [devSettings]);

  const calculateDistance = (p1: GeoLocation, p2: GeoLocation) => {
    const R = 3958.8;
    const dLat = (p2.lat - p1.lat) * Math.PI / 180;
    const dLng = (p2.lng - p1.lng) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(p1.lat * Math.PI / 180) * Math.cos(p2.lat * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const handleConfirmIncident = useCallback((incidentId: string) => {
    if (!userLocation) return;
    setIncidents(prev => {
      const updated = prev.map(inc => {
        if (inc.id === incidentId) {
          return {
            ...inc,
            isConfirmed: true,
            distance: calculateDistance(userLocation, inc.location)
          };
        }
        return inc;
      });
      saveIncidents(updated);
      return updated;
    });
  }, [userLocation]);

  const syncBrain = useCallback(async (loc: GeoLocation) => {
    if (backoffActive) {
      setStatusText("NEURAL COOLING...");
      return;
    }
    setIsScanning(true);
    setStatusText("SYNCING ACTIVE DISPATCH...");
    try {
      if (!canUseAI) { setStatusText("UPGRADE TO TACTICAL FOR AI THREAT ANALYSIS"); setIsScanning(false); setIsSubModalOpen(true); return; }
      const rawIncidents = await fetchLiveThreats(loc, canUseOwnKey ? userGeminiKey : undefined);
      logger.info('SyncBrain successful', { count: rawIncidents.length });
      
      setIncidents(prev => {
        const newIncidents = rawIncidents.filter(
          raw => !prev.some(p => p.id === raw.id)
        ).map(inc => ({
          ...inc,
          isConfirmed: true,
          distance: calculateDistance(loc, inc.location)
        }));
        
        const allIncidents = [...prev, ...newIncidents]
          .filter(inc => isIncidentRecent(inc, MAX_DATA_AGE_MINUTES))
          .sort((a, b) => b.timestamp - a.timestamp)
          .slice(0, 100); // Keep up to 100 in the master log
          
        saveIncidents(allIncidents);

        // Sync to Firestore if user is logged in
        if (user) {
          newIncidents.forEach(inc => {
            setDoc(doc(db, 'users', user.uid, 'incidents', inc.id), inc);
          });
        }

        return allIncidents;
      });
      
      setStatusText("PERIMETER NOMINAL");
    } catch (err: any) {
      logger.error('SyncBrain failed', { error: err.message });
      if (err.message === "RATE_LIMIT_EXHAUSTED") {
        setBackoffActive(true);
        setStatusText("QUOTA EXHAUSTED - PAUSING SYNC");
        setTimeout(() => setBackoffActive(false), 120000);
      } else {
        setStatusText("SYNC FAILED - CHECK CONNECTION");
        console.error("Sync Brain Error:", err);
      }
    } finally {
      setIsScanning(false);
    }
  }, [backoffActive, user, userGeminiKey]);

  useEffect(() => {
    if (!userLocation || !incidents || incidents.length === 0 || !proximityAlarmEnabled) {
      setInDangerZone(false);
      setClosestDanger(null);
      return;
    }

    // Only consider incidents with a real, finite distance.
    // Previous logic used (inc.distance || 0) which treated missing/zero distances as 0 mi —
    // any incident lacking a calculated distance instantly fired the proximity alarm.
    const immediateThreats = incidents.filter(inc =>
      typeof inc.distance === 'number' &&
      isFinite(inc.distance) &&
      inc.distance > 0 &&
      inc.distance <= dangerRadius
    );

    if (immediateThreats.length > 0) {
      const highest = immediateThreats[0];
      const now = Date.now();
      
      if (!inDangerZone) {
        setInDangerZone(true);
        setIsLogVisible(true);
        setStatusText("!!! CRITICAL THREAT ALERT !!!");
      } else if (closestDanger?.id !== highest.id && highest.threatLevel === ThreatLevel.CRITICAL) {
        setIsLogVisible(true);
        setStatusText("!!! NEW CRITICAL THREAT DETECTED !!!");
      }
      
      setClosestDanger(highest);

      // Tactical Alert Sound Logic: 60s cooldown and specific source/threat verification
      if (highest.threatLevel === ThreatLevel.CRITICAL && 
          (highest.source === 'RADIO' || highest.source === 'DISPATCH') &&
          now - lastAlertTimeRef.current > 60000) {
        playTacticalAlert(`Critical threat detected: ${highest.type}. Tactical evasion is strongly advised.`);
        lastAlertTimeRef.current = now;
      }
    } else {
      if (inDangerZone) {
        setInDangerZone(false);
        setClosestDanger(null);
        setStatusText("PERIMETER NOMINAL");
      }
    }
  }, [userLocation, incidents, inDangerZone, proximityAlarmEnabled, closestDanger]);

  useEffect(() => {
    if (manualLocationMode) return;
    if (geoAttemptedRef.current) return;
    geoAttemptedRef.current = true;

    const handleGeoSuccess = (pos: GeolocationPosition) => {
      const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = window.setTimeout(() => {
        setUserLocation(loc);
      }, 1000);
      if (incidents.length === 0) {
        syncBrain(loc);
      }
    };

    const handleGeoError = (err: GeolocationPositionError) => {
      // Suppress console errors for expected permission denials in iframes
      if (err.code === 1) {
        console.warn(`[GEOLOCATION] Permission denied or disabled by policy. Switching to manual mode.`);
      } else if (err.code === 3) {
        console.warn(`[GEOLOCATION WARNING] Code: ${err.code} | Message: ${err.message}`);
      } else {
        console.error(`[GEOLOCATION ERROR] Code: ${err.code} | Message: ${err.message}`);
      }
      
      // If we already have a location, don't force manual mode on timeout
      if (err.code === 3 && userLocation) {
        setStatusText(`GPS SIGNAL WEAK - USING LAST KNOWN`);
        return;
      }

      if (err.code === 3) { // Timeout
        setStatusText(`GPS SIGNAL WEAK - RETRYING...`);
        // Try again with lower accuracy immediately
        navigator.geolocation.getCurrentPosition(
          handleGeoSuccess,
          (err2) => {
            if (err2.code !== 1) console.error(`[GEOLOCATION FALLBACK ERROR] Code: ${err2.code} | Message: ${err2.message}`);
            setStatusText(`GPS TIMED OUT - MANUAL OVERRIDE ENABLED`);
            setManualLocationMode(true);
          },
          { enableHighAccuracy: false, timeout: 15000 }
        );
      } else {
        setStatusText("DOWNTOWN CIVIC RADAR // ACTIVE");
        setManualLocationMode(false);
        if (watchIdRef.current) {
          navigator.geolocation.clearWatch(watchIdRef.current);
          watchIdRef.current = null;
        }
      }
    };

    if (navigator.geolocation) {
      if (!userLocation) {
        setStatusText("ACQUIRING GPS SIGNAL...");
      }
      
      // Initial attempt
      navigator.geolocation.getCurrentPosition(
        handleGeoSuccess,
        handleGeoError,
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );

      // Continuous tracking
      watchIdRef.current = navigator.geolocation.watchPosition(
        handleGeoSuccess,
        (err) => {
          if (err.code === 1) {
            console.warn(`[WATCH GEOLOCATION] Permission denied. Defaulting to Downtown Tucson.`);
            setManualLocationMode(false);
            setUserLocation({ lat: 32.2226, lng: -110.9747 });
            if (watchIdRef.current) {
              navigator.geolocation.clearWatch(watchIdRef.current);
              watchIdRef.current = null;
            }
          } else if (err.code !== 3) { // Only log non-timeout errors for watchPosition to reduce noise
            console.error(`[WATCH GEOLOCATION ERROR] Code: ${err.code} | Message: ${err.message}`);
          }
        },
        { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
      );
    } else {
      setStatusText("GEOLOCATION NOT SUPPORTED - MANUAL OVERRIDE ENABLED");
      setManualLocationMode(true);
    }
    scanTimerRef.current = window.setInterval(() => {
      if (userLocation) syncBrain(userLocation);
    }, devSettings.syncInterval);
    return () => {
      if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
      if (scanTimerRef.current) clearInterval(scanTimerRef.current);
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [syncBrain, userLocation, manualLocationMode, incidents?.length, devSettings.syncInterval]);

  const toggleManualMode = () => {
    setManualLocationMode(!manualLocationMode);
    if (!manualLocationMode) {
      setStatusText("MANUAL OVERRIDE ACTIVE");
    } else {
      setStatusText("GPS SYNC ACTIVE");
    }
  };

  const resetManualLocation = () => {
    setManualLocationMode(false);
    setUserLocation(null);
    localStorage.removeItem('userLocation');
    setStatusText("GPS SYNC RE-INITIALIZING...");
  };

  const handleLocationSubmit = async () => {
    const trimmedInput = locationInput.trim();
    if (!trimmedInput) {
      setLocationError("PLEASE ENTER A VALID SECTOR ADDRESS");
      return;
    }

    // Basic coordinate validation (lat, lng)
    const coordRegex = /^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?$/;
    if (coordRegex.test(trimmedInput)) {
      const [lat, lng] = trimmedInput.split(',').map(s => parseFloat(s.trim()));
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        setLocationError("COORDINATES OUT OF BOUNDS");
        return;
      }
      setLocationError(null);
      setStatusText("MANUAL COORDINATES LOCKED");
      const coords = { lat, lng };
      setUserLocation(coords);
      setIsLocationSet(true);
      setStatusText(`LOCATION SET: ${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}`);
      syncBrain(coords);
      return;
    }

    // Address geocoding
    setLocationError(null);
    setStatusText("GEOCODING SECTOR ADDRESS...");
    const coords = await geocodeAddress(trimmedInput, canUseOwnKey ? userGeminiKey : undefined);
    if (coords) {
      setUserLocation(coords);
      setIsLocationSet(true);
      setStatusText(`LOCATION SET: ${trimmedInput.toUpperCase()}`);
      syncBrain(coords);
    } else {
      setLocationError("GEOCODING FAILED - SECTOR UNKNOWN");
      setStatusText("GEOCODING FAILED - TRY AGAIN");
    }
  };

  const handleLocationReset = () => {
    setIsLocationSet(false);
    setLocationInput('');
    setStatusText("LOCATION RESET - AWAITING INPUT");
  };

  const handleGenerateConcept = async () => {
    if (!userLocation) return;
    setIsGeneratingConcept(true);
    setStatusText("GENERATING CYBER-WEST VISION...");
    try {
      if (!canUseAI) { setStatusText("UPGRADE TO TACTICAL FOR AI ART"); setIsGeneratingConcept(false); setIsSubModalOpen(true); return; }
      const img = await generateWildWestConcept(locationInput || "Tucson, AZ", canUseOwnKey ? userGeminiKey : undefined, visionPrompt);
      if (img) {
        setConceptImage(img);
        setStatusText("VISION GENERATED");
      } else {
        setStatusText("VISION GENERATION FAILED");
      }
    } catch (e) {
      setStatusText("VISION GENERATION ERROR");
    } finally {
      setIsGeneratingConcept(false);
    }
  };

  const handleMapClick = (loc: GeoLocation) => {
    if (manualLocationMode) {
      setUserLocation(loc);
      syncBrain(loc);
      setStatusText("MANUAL LOCATION SET");
    }
  };

  const handleBusinessClick = (feature: any) => {
    setSelectedBusiness(feature);
  };

  const handleIncidentSelect = async (inc: Incident) => {
    if (vicinityAnalysisCooldown > 0) {
      setStatusText(`SCAN READY IN ${vicinityAnalysisCooldown}s`);
      return;
    }
    
    setSelectedIncident(inc);
    setHighlightedIncidentId(inc.id);
    setTimeout(() => setHighlightedIncidentId(null), 5000);
    setVicinityReport("Analyzing neighborhood heat...");
    setStatusText("ANALYZING VICINITY...");
    
    try {
      if (!canUseAI) { setVicinityReport("🔒 Vicinity analysis requires TACTICAL clearance."); setStatusText("UPGRADE REQUIRED"); setIsSubModalOpen(true); return; }
      const report = await analyzeIncidentVicinity(inc.location, canUseOwnKey ? userGeminiKey : undefined);
      setVicinityReport(report);
      setStatusText("ANALYSIS COMPLETE");
      setVicinityAnalysisCooldown(60);
    } catch (err: any) {
      setVicinityReport("Vicinity intel unavailable.");
      setStatusText("ANALYSIS FAILED");
    }
  };
  
  const handleMapIncidentClick = (incident: Incident) => {
    handleIncidentSelect(incident);
    setIsLogVisible(true);
    setHighlightedIncidentId(incident.id);
    setTimeout(() => setHighlightedIncidentId(null), 2000);
  };

  const handleNewRadioIncident = useCallback((inc: Incident) => {
    setIncidents(prev => {
      if (prev.some(p => p.id === inc.id)) return prev;

      // Audio alert for new incidents
      try {
        if (inc.threatLevel === 'CRITICAL') playAlertTone('CRITICAL');
        else if (inc.threatLevel === 'HIGH') playAlertTone('HIGH');
        else playDispatchBeep();
      } catch {}

      const updated = [inc, ...prev]
        .filter(i => isIncidentRecent(i, MAX_DATA_AGE_MINUTES))
        .slice(0, 100);
      saveIncidents(updated);
      return updated;
    });
  }, []);

  const handleSummarize = useCallback(async (incidentId: string, dispatchText: string) => {
    if (backoffActive) return;
    
    const now = Date.now();
    if (now - lastSummarizeRef.current < devSettings.summarizeCooldown) {
      const remaining = Math.ceil((devSettings.summarizeCooldown - (now - lastSummarizeRef.current)) / 1000);
      setStatusText(`Action on cooldown. Try again in ${remaining} seconds.`);
      return;
    }

    setSummarizingIncidentId(incidentId);
    lastSummarizeRef.current = now;
    
    try {
      if (!canUseAI) { setStatusText("UPGRADE TO TACTICAL FOR AI SUMMARIES"); setIsSubModalOpen(true); setSummarizingIncidentId(null); return; }
      const summary = await summarizeDispatch(dispatchText, canUseOwnKey ? userGeminiKey : undefined);
      setIncidents(prev => prev.map(inc => inc.id === incidentId ? { ...inc, summary } : inc));
      setStatusText("SUMMARY GENERATED");
    } catch (err) {
      setStatusText("SUMMARY FAILED");
    } finally {
      setSummarizingIncidentId(null);
    }
  }, [backoffActive, userGeminiKey]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    isResizingRef.current = true;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizingRef.current) return;
    const newWidth = window.innerWidth - e.clientX;
    if (newWidth > 300 && newWidth < 800) { // Set min/max resize bounds
      setLogWidth(newWidth);
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    isResizingRef.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const handleDragStart = (e: React.MouseEvent) => {
    if (!isLogDetached) return;
    setIsDragging(true);
    dragOffset.current = {
      x: e.clientX - logPosition.x,
      y: e.clientY - logPosition.y
    };
    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);
  };

  const handleDragMove = useCallback((e: MouseEvent) => {
    setLogPosition({
      x: e.clientX - dragOffset.current.x,
      y: e.clientY - dragOffset.current.y
    });
  }, []);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    document.removeEventListener('mousemove', handleDragMove);
    document.removeEventListener('mouseup', handleDragEnd);
  }, [handleDragMove]);

  const exportMasterLogCSV = () => {
    const sortedIncidents = [...incidents].sort((a, b) => a.timestamp - b.timestamp);
    const csvData = sortedIncidents.map(inc => ({
      ID: inc.id,
      TimeReceived: new Date(inc.timestamp).toLocaleString(),
      Type: inc.type,
      ThreatLevel: inc.threatLevel,
      Source: inc.source,
      Description: inc.description,
      Summary: inc.summary || '',
      Latitude: inc.location.lat,
      Longitude: inc.location.lng,
      DistanceMeasured: inc.distance ? inc.distance.toFixed(2) : 'N/A',
      Confirmed: inc.isConfirmed ? 'Yes' : 'No'
    }));
    
    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `master_log_${new Date().toISOString()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleLog = () => {
    if (isLogVisible) {
      setIsLogGhost(false);
    }
    setIsLogVisible(!isLogVisible);
  };

  const handleVersionClick = () => {
    setVersionClickCount(prev => {
      if (prev + 1 >= 7) {
        setIsDevToolsOpen(true);
        return 0;
      }
      return prev + 1;
    });
  };

  // Check for log-only mode (for pop-out window)
  const isLogOnly = new URLSearchParams(window.location.search).get('mode') === 'log';
  const ghostParam = new URLSearchParams(window.location.search).get('isGhost') === 'true';
  const opacityParam = Number(new URLSearchParams(window.location.search).get('opacity')) || 75;

  // /app/admin → render the admin shell instead of the normal map. Auth +
  // admin-claim gating happens inside AdminApp itself.
  const path = window.location.pathname;
  if (path === '/app/admin' || path === '/app/admin/' || path.endsWith('/admin')) {
    const AdminApp = React.lazy(() => import('./components/admin/AdminApp'));
    return (
      <React.Suspense fallback={<div className="h-screen w-screen flex items-center justify-center bg-slate-950 text-slate-400">loading admin…</div>}>
        <AdminApp />
      </React.Suspense>
    );
  }

  if (isLogOnly) {
    return (
      <div className="h-screen w-screen bg-black overflow-hidden">
        <IncidentLog 
          incidents={incidents} 
          onSelect={handleIncidentSelect} 
          onSummarize={handleSummarize} 
          summarizingIncidentId={summarizingIncidentId} 
          globalBackoffActive={backoffActive} 
          onClose={() => window.close()}
          selectedIncident={selectedIncident}
          vicinityReport={vicinityReport}
          theme={devSettings.theme}
          isDetached={true}
          isGhost={ghostParam}
          logGhostOpacity={opacityParam}
          onToggleGhost={() => {}}
          onSetGhostOpacity={() => {}}
          highlightedIncidentId={highlightedIncidentId}
        />
      </div>
    );
  }

  return (
    <div className="relative h-screen w-screen overflow-hidden select-none text-white">
      {authError && (
        <div style={{position:'fixed',top:0,left:0,right:0,zIndex:99999,background:'#330000',borderBottom:'2px solid #ff3333',padding:'12px',color:'#ffdddd',fontFamily:'monospace',fontSize:'13px',boxShadow:'0 4px 12px rgba(0,0,0,0.6)'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'6px'}}>
            <strong style={{color:'#ff6666'}}>AUTH ERROR</strong>
            <div style={{display:'flex',gap:'8px'}}>
              <button onClick={()=>{ try{ navigator.clipboard.writeText(`${authError.code}\n${authError.message}`); }catch(e){} }} style={{background:'#660000',border:'1px solid #ff3333',color:'#ffdddd',padding:'4px 10px',fontFamily:'monospace',fontSize:'11px',cursor:'pointer'}}>COPY</button>
              <button onClick={()=>setAuthError(null)} style={{background:'#660000',border:'1px solid #ff3333',color:'#ffdddd',padding:'4px 10px',fontFamily:'monospace',fontSize:'11px',cursor:'pointer'}}>CLOSE</button>
            </div>
          </div>
          <textarea readOnly value={`${authError.code}\n${authError.message}`} onClick={(e)=>(e.target as HTMLTextAreaElement).select()} style={{width:'100%',minHeight:'80px',background:'#1a0000',color:'#ffdddd',border:'1px solid #660000',padding:'8px',fontFamily:'monospace',fontSize:'12px',userSelect:'text',WebkitUserSelect:'text',resize:'vertical'}} />
        </div>
      )}

      {/* ── SPONSOR BEACON MONETIZATION MODAL ($25/MO) ── */}
      {isSponsorAdModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-md p-4 font-mono">
          <div className="bg-[#080c14] border-2 border-emerald-400 p-6 sm:p-8 rounded-2xl max-w-lg w-full shadow-[0_0_50px_rgba(16,185,129,0.3)] relative">
            <button
              onClick={() => setIsSponsorAdModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white text-lg"
            >
              ✕
            </button>

            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-950/60 border border-emerald-400/50 text-emerald-300 text-xs font-bold uppercase tracking-wider mb-4">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              TUCSON BUSINESS ADVERTISING
            </div>

            <h3 className="text-2xl font-black text-white font-orbitron mb-3">
              Put Your Business on the <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">Live 3D Radar</span>
            </h3>

            <p className="text-xs text-gray-300 leading-relaxed mb-5">
              Thousands of Tucson locals monitor this live map 24/7. For <strong>$25/month</strong>, place a verified 3D beacon with your storefront photo or logo right on your building.
            </p>

            <div className="space-y-2.5 mb-6 text-xs">
              <div className="p-3 rounded-xl bg-white/5 border border-white/10 flex items-start gap-3">
                <span className="text-lg">📸</span>
                <div>
                  <div className="text-white font-bold">Custom Storefront Card</div>
                  <div className="text-gray-400 text-[11px]">Your business image, logo, and hours float directly over your address.</div>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-white/5 border border-white/10 flex items-start gap-3">
                <span className="text-lg">⚡</span>
                <div>
                  <div className="text-white font-bold">Proximity Radar Highlight</div>
                  <div className="text-gray-400 text-[11px]">When 911 calls or incidents occur near you, your beacon pulses and alerts users.</div>
                </div>
              </div>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const bName = (form.elements[0] as HTMLInputElement).value;
                const bAddr = (form.elements[1] as HTMLInputElement).value;
                const bImg = (form.elements[2] as HTMLInputElement).value;
                try {
                  const res = await fetch('/api/stripe/create-beacon-checkout', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ businessName: bName, address: bAddr, imageUrl: bImg })
                  });
                  const data = await res.json();
                  if (data.url) {
                    window.location.href = data.url;
                  } else {
                    alert('Checkout error: ' + (data.error || 'Failed to start payment'));
                  }
                } catch (err: any) {
                  alert('Network error starting Stripe checkout: ' + err.message);
                }
              }}
              className="space-y-3 text-xs"
            >
              <div>
                <label className="block text-gray-400 mb-1">BUSINESS OR EVENT NAME</label>
                <input required placeholder="e.g. Desert Bloom Coffee / Open House" className="w-full bg-black/60 border border-white/20 rounded p-2 text-white focus:border-emerald-400 outline-none" />
              </div>
              <div>
                <label className="block text-gray-400 mb-1">STREET ADDRESS OR CORRIDOR</label>
                <input required placeholder="e.g. 1400 N 4th Ave, Tucson" className="w-full bg-black/60 border border-white/20 rounded p-2 text-white focus:border-emerald-400 outline-none" />
              </div>
              <div>
                <label className="block text-gray-400 mb-1">STOREFRONT IMAGE URL</label>
                <input placeholder="https://yourwebsite.com/storefront.jpg" className="w-full bg-black/60 border border-white/20 rounded p-2 text-white focus:border-emerald-400 outline-none" />
              </div>

              <button
                type="submit"
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 text-black font-extrabold uppercase text-xs tracking-wider shadow-[0_0_20px_rgba(16,185,129,0.5)] hover:brightness-110 transition-all mt-4"
              >
                ACTIVATE 3D BEACON ($25/MO) &rarr;
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── Global modals ── */}
      {isSubModalOpen && (
        <SubscriptionModal
          userId={user?.uid ?? null}
          currentTier={tier}
          onSubscribed={(newTier, subId) => { activateTier(newTier, subId); setIsSubModalOpen(false); }}
          onClose={() => setIsSubModalOpen(false)}
        />
      )}
      {canUseDanger && <DangerAlert isVisible={inDangerZone} distance={closestDanger?.distance || 0} type={closestDanger?.type || "POLICE ACTIVITY"} theme={devSettings.theme} />}
      {isSafetyOpen && <SafetyCompanion userId={user?.uid ?? null} userLocation={userLocation} tier={tier} onClose={() => setIsSafetyOpen(false)} onUpgrade={() => { setIsSafetyOpen(false); setIsSubModalOpen(true); }} />}
      {selectedBusiness && <BusinessBillboard business={selectedBusiness} onClose={() => setSelectedBusiness(null)} theme={devSettings.theme} />}
      {isDevToolsOpen && <DevTools settings={devSettings} onUpdate={setDevSettings} onClose={() => setIsDevToolsOpen(false)} userGeminiKey={userGeminiKey} setUserGeminiKey={setUserGeminiKey} isPaidSubscriber={canUseOwnKey} setIsPaidSubscriber={() => setIsSubModalOpen(true)} currentTier={tier} onTierChange={(t) => activateTier(t, 'admin-override')} />}
      {isChatOpen && !hasAgreedToToS && <AgeGateAndToS onAgree={() => setHasAgreedToToS(true)} />}
      {isChatOpen && hasAgreedToToS && <ChatRoom onClose={() => setIsChatOpen(false)} isDetached={isChatDetached} onToggleDetach={() => setIsChatDetached(!isChatDetached)} />}

      {/* Community feed — toggle via Newspaper button in HUD. SSE delivers
          the just-created post back to all open feeds, so the composer
          doesn't need to plumb back into the feed manually. */}
      {isFeedOpen && (
        <Feed
          isSignedIn={!!user}
          isAdmin={!!user && (user.email === 'desertshibari@gmail.com')}
          selfUid={user?.uid || null}
          userLocation={userLocation}
          onClose={() => setIsFeedOpen(false)}
          onCompose={async () => {
            if (!user) {
              try { await signInWithGoogle(); } catch {}
              return;
            }
            setIsComposerOpen(true);
          }}
          onLocate={(lat, lng) => {
            setHighlightedIncidentId(null);
            setVenueTarget({ lat, lng, name: '' });
          }}
          onSignInRequired={async () => { try { await signInWithGoogle(); } catch {} }}
        />
      )}
      <PostComposer
        open={isComposerOpen}
        onClose={() => setIsComposerOpen(false)}
        onCreated={() => { /* SSE handles propagation into the feed and map */ }}
        userLocation={userLocation}
        userRole={myProfile?.role}
      />
      <TrustedSourceApplyModal
        open={isTrustedApplyOpen}
        onClose={() => setIsTrustedApplyOpen(false)}
      />
      <ProfileDialog isOpen={isProfileOpen} onClose={() => setIsProfileOpen(false)} />
      <NotificationSettings
        open={isNotifSettingsOpen}
        onClose={() => setIsNotifSettingsOpen(false)}
        userLocation={userLocation}
      />

      {/* Floating Post FAB — visible only when feed is closed (composer needs sign-in) */}
      {!isFeedOpen && (
        <button
          onClick={async () => {
            if (!user) { try { await signInWithGoogle(); } catch {} return; }
            setIsComposerOpen(true);
          }}
          className="fixed bottom-24 right-4 z-30 inline-flex items-center gap-2 px-4 py-3 rounded-full bg-amber-600 hover:bg-amber-500 text-slate-950 font-bold shadow-lg shadow-amber-900/40 transition"
          title="Report an incident"
        >
          <PenSquare size={16} /> POST
        </button>
      )}

      {/* Sidebar banner ad — bottom-right above the radio feed strip. Hidden
          when Feed is open (avoid double-ads + cluttering the slide-in pane).
          Server returns null for premium users so this disappears for them. */}
      {!isFeedOpen && (
        <div className="fixed bottom-44 right-4 z-20 max-w-[260px] hidden md:block pointer-events-auto">
          <AdSlot slot="sidebar_banner" userLocation={userLocation} variant="sidebar"/>
        </div>
      )}

      {/* ── Fullscreen map — base layer ── */}
      <TacticalMap
        userLocation={userLocation}
        incidents={incidents}
        onIncidentClick={handleMapIncidentClick}
        onBusinessClick={handleBusinessClick}
        onMapClick={handleMapClick}
        theme={devSettings.theme}
        highlightedIncidentId={highlightedIncidentId}
        aryaLocation={aryaLocation}
        venueTarget={venueTarget}
        eventPins={eventPins}
        sponsoredPins={sponsoredPins}
      />

      {/* ── Floating radio feed (bottom strip) ── */}
      <div className="absolute bottom-8 left-14 right-0 z-30 max-h-52 overflow-hidden pointer-events-none">
        <div className="pointer-events-auto">
          <RadioFeed onNewIncident={handleNewRadioIncident} userLocation={userLocation} tier={tier} onUpgradeClick={() => setIsSubModalOpen(true)} onAryaCall={(loc) => setAryaLocation(loc)} onCallClick={(lat, lon, address) => setVenueTarget({ lat, lng: lon, name: address })} />
        </div>
      </div>

      {/* Floating Live Radio Player */}
      <RadioPlayer />

      {/* ── TOP HUD BAR (was <header>) ── */}
      <div className="absolute top-0 left-0 right-0 z-40 h-14 px-2 sm:px-4 flex items-center justify-between gap-2 overflow-hidden"
        style={{ background: 'rgba(4,1,8,0.82)', backdropFilter: 'blur(14px)', borderBottom: '1px solid rgba(212,168,67,0.22)' }}
      >
        {/* Logo */}
        <div className="flex items-center gap-2 shrink-0">
          <div className={`p-1.5 rounded-lg transition-all duration-500 ${inDangerZone ? 'shadow-lg' : ''}`} style={{ backgroundColor: inDangerZone ? devSettings.theme.danger : 'transparent', boxShadow: inDangerZone ? `0 0 15px ${devSettings.theme.danger}` : 'none' }}>
            <Terminal size={20} className="text-[#d4a843]" />
          </div>
          <div className="hidden sm:block">
            <h1 className="font-orbitron text-xs font-bold tracking-tight text-[#d4a843] flex items-center gap-1 uppercase whitespace-nowrap">
              <button onClick={toggleLog} className={`p-0.5 rounded transition-colors ${isLogVisible ? 'text-[#00e5ff]' : 'text-[#d4a843]'}`}>
                <Bell size={13} className={isScanning ? 'animate-pulse' : ''} />
              </button>
              TUCSON STREETS
            </h1>
            <div className="flex items-center gap-1 font-mono">
              <span className={`w-1 h-1 rounded-full ${isScanning ? 'animate-ping' : inDangerZone ? 'animate-pulse' : ''}`} style={{ backgroundColor: isScanning ? devSettings.theme.primary : inDangerZone ? devSettings.theme.danger : '#d4a843' }}></span>
              <span title={statusText} className="text-[8px] tracking-widest text-[#d4a843] truncate max-w-[120px]" style={{ color: inDangerZone ? devSettings.theme.danger : '#d4a843' }}>{statusText}</span>
            </div>
          </div>
          {/* Mobile-only bell */}
          <button onClick={toggleLog} className={`sm:hidden p-1 rounded transition-colors ${isLogVisible ? 'text-[#00e5ff]' : 'text-[#d4a843]'}`}>
            <Bell size={16} className={isScanning ? 'animate-pulse' : ''} />
          </button>
        </div>

        {/* Location input — shrinks on small screens */}
        <div className="flex items-center gap-1 flex-1 min-w-0 mx-1">
          {!isLocationSet ? (
            <div className="flex flex-col relative w-full max-w-xs">
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  value={locationInput}
                  onChange={(e) => { setLocationInput(e.target.value); if (locationError) setLocationError(null); }}
                  onKeyDown={(e) => e.key === 'Enter' && handleLocationSubmit()}
                  placeholder="COORDS / ADDRESS..."
                  className={`terminal-input text-[10px] px-2 py-1 rounded-sm w-full min-w-0 ${locationError ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                />
                <button onClick={handleLocationSubmit} className="text-[10px] bg-[#d4a843]/20 hover:bg-[#d4a843]/40 border border-[#d4a843]/50 px-2 py-1 rounded-sm text-[#d4a843] font-bold font-mono shrink-0">GO</button>
              </div>
              {locationError && (
                <div className="absolute top-full left-0 mt-1 text-[8px] text-red-500 font-bold animate-pulse flex items-center gap-1 bg-black/80 px-1 py-0.5 rounded border border-red-500/30 z-50 whitespace-nowrap">
                  <AlertTriangle size={8} /> {locationError}
                </div>
              )}
            </div>
          ) : (
            <button onClick={handleLocationReset} className="text-[10px] bg-red-900/40 hover:bg-red-900/60 border border-red-500/50 px-2 py-1 rounded-sm text-red-400 font-bold font-mono shrink-0">RESET</button>
          )}
        </div>

        {/* Right controls */}
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => setProximityAlarmEnabled(!proximityAlarmEnabled)}
            className={`p-1.5 rounded-sm border transition-colors ${proximityAlarmEnabled ? 'bg-red-900/40 border-red-500/50 text-red-400' : 'bg-black border-[#d4a843]/30 text-[#d4a843]'}`}
            title={`Proximity Alarm ${proximityAlarmEnabled ? 'ON' : 'OFF'}`}
          >
            <AlertTriangle size={15} className={proximityAlarmEnabled ? 'animate-pulse' : ''} />
          </button>
          <button onClick={toggleManualMode}
            className={`p-1.5 rounded-sm border transition-colors ${manualLocationMode ? 'border-[#d4a843]/50 text-[#d4a843]' : 'bg-black border-[#d4a843]/30 text-[#d4a843]'}`}
            title="Manual Location"
          >
            <Target size={15} className={manualLocationMode ? 'animate-pulse' : ''} />
          </button>
          <button onClick={() => setIsChatOpen(true)} className="p-1.5 rounded-sm border bg-black border-[#d4a843]/30 text-[#d4a843] transition-colors" title="Chat">
            <MessageSquare size={15} />
          </button>
          <button onClick={() => setIsLogVisible(!isLogVisible)} className="p-1.5 rounded-sm text-[#d4a843] transition-colors" title="Incident Log">
            <PanelRightOpen size={15} />
          </button>
          <button
            onClick={() => setIsFeedOpen(o => !o)}
            className={`p-1.5 rounded-sm border transition-colors ${isFeedOpen ? 'bg-[#d4a843]/20 border-[#d4a843]/50 text-[#d4a843]' : 'bg-black border-[#d4a843]/30 text-[#d4a843]'}`}
            title="Community Feed"
          >
            <Newspaper size={15} />
          </button>
          {user ? (
            <>
              <button onClick={() => setIsProfileOpen(true)}
                className="p-1.5 rounded-sm border bg-black border-[#d4a843]/30 text-[#d4a843] transition-colors"
                title="Profile (display name, handle, neighborhood)">
                <UserIcon size={15} />
              </button>
              <button onClick={() => setIsNotifSettingsOpen(true)}
                className="p-1.5 rounded-sm border bg-black border-[#d4a843]/30 text-[#d4a843] transition-colors"
                title="Notification settings">
                <Bell size={15} />
              </button>
              <button onClick={() => setIsTrustedApplyOpen(true)}
                className="p-1.5 rounded-sm border bg-black border-[#d4a843]/30 text-[#d4a843] transition-colors"
                title="Apply for Trusted Source Partnership">
                <ShieldAlert size={15} />
              </button>
              <button onClick={logOut} className="p-1.5 rounded-sm border bg-black border-[#d4a843]/30 text-[#d4a843] transition-colors" title="Log Out">
                <LogOut size={15} />
              </button>
            </>
          ) : (
            <button
              onClick={async () => {
                try { await signInWithGoogle(); }
                catch (err: any) {
                  const code = err?.code || '';
                  if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
                    setStatusText(`AUTH ERROR: ${code || 'unknown'}`);
                    setAuthError({ code: code || 'unknown', message: err?.message || String(err) });
                    console.error('[auth]', err);
                  }
                }
              }}
              className="flex items-center gap-1 px-2 py-1 rounded-sm border bg-[#d4a843]/20 border-[#d4a843]/50 text-[#d4a843] text-[10px] font-bold font-mono shrink-0">
              <LogIn size={13} />
              <span className="hidden sm:inline">AUTH</span>
            </button>
          )}
        </div>
      </div>{/* end top HUD bar */}

      {/* ── LEFT NAV PILL (was <aside>) ── */}
      <div className="absolute left-0 top-14 bottom-8 z-40 w-14 flex flex-col items-center py-5 gap-7"
        style={{ background: 'rgba(4,1,8,0.78)', backdropFilter: 'blur(12px)', borderRight: '1px solid rgba(212,168,67,0.15)' }}
      >
          <button 
            onClick={() => {
              setActiveTab('threats');
              if (!isLogVisible) setIsLogVisible(true);
            }}
            className={`flex flex-col items-center gap-1 transition-colors ${isLogVisible && activeTab === 'threats' ? 'text-[#d4a843]' : 'text-[#d4a843] hover:text-[#d4a843]'}`}
          >
            <Zap size={20} className={inDangerZone ? 'animate-bounce text-red-500' : ''} />
            <span className="text-[8px] font-bold uppercase font-mono">Threat</span>
          </button>
          <button 
            onClick={() => {
              setActiveTab('alerts');
              if (!isLogVisible) setIsLogVisible(true);
            }}
            className={`flex flex-col items-center gap-1 transition-colors ${isLogVisible && activeTab === 'alerts' ? 'text-[#d4a843]' : 'text-[#d4a843] hover:text-[#d4a843]'}`}
          >
            <Bell size={20} className={isScanning ? 'animate-pulse' : ''} />
            <span className="text-[8px] font-bold uppercase font-mono">Dispatch</span>
          </button>
          <button 
            onClick={() => {
              setIsAlarmPanelOpen(!isAlarmPanelOpen);
              if (!isLogVisible) setIsLogVisible(true);
            }}
            className={`flex flex-col items-center gap-1 transition-colors ${isLogVisible && isAlarmPanelOpen ? 'text-[#d4a843]' : 'text-[#d4a843] hover:text-[#d4a843]'}`}
          >
            <ShieldAlert size={20} />
            <span className="text-[8px] font-bold uppercase font-mono">Alarm</span>
          </button>
          <button
            onClick={() => setIsSafetyOpen(true)}
            className="flex flex-col items-center gap-1 transition-colors relative text-emerald-400 hover:text-emerald-300"
          >
            <Shield size={20} />
            <span className="text-[8px] font-bold uppercase font-mono">Safety</span>
            {!canUseAI && <span className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full text-[6px] text-black font-bold flex items-center justify-center">$</span>}
          </button>
          <button
            onClick={() => {
              if (!canUseAI) { setIsSubModalOpen(true); return; }
              setActiveTab('intel');
              if (!isLogVisible) setIsLogVisible(true);
            }}
            className={`flex flex-col items-center gap-1 transition-colors relative ${isLogVisible && activeTab === 'intel' ? 'text-[#d4a843]' : 'text-[#d4a843] hover:text-[#d4a843]'}`}
          >
            <Info size={20} />
            <span className="text-[8px] font-bold uppercase font-mono">Intel</span>
            {!canUseAI && <span className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full text-[6px] text-black font-bold flex items-center justify-center">$</span>}
          </button>
          <button
            onClick={() => {
              if (!canUseGameMode) { setIsSubModalOpen(true); return; }
              setActiveTab('game');
              if (!isLogVisible) setIsLogVisible(true);
            }}
            className={`flex flex-col items-center gap-1 transition-colors relative ${isLogVisible && activeTab === 'game' ? 'text-[#d4a843]' : 'text-[#d4a843] hover:text-[#d4a843]'}`}
          >
            <Gamepad2 size={20} className={activeTab === 'game' ? 'text-[#d4a843]' : ''} />
            <span className="text-[8px] font-bold uppercase font-mono">Game</span>
            {!canUseGameMode && <span className="absolute -top-1 -right-1 w-3 h-3 bg-purple-500 rounded-full text-[6px] text-white font-bold flex items-center justify-center">$</span>}
          </button>
          <button 
            onClick={() => setIsVisionModalOpen(true)}
            disabled={isGeneratingConcept || !userLocation}
            className={`flex flex-col items-center gap-1 transition-colors ${isVisionModalOpen ? 'text-[#d4a843]' : 'text-[#d4a843] hover:text-[#d4a843] disabled:opacity-50'}`}
          >
            <Eye size={20} />
            <span className="text-[8px] font-bold uppercase font-mono">Vision</span>
          </button>
          <div className="mt-auto mb-2" onClick={() => !backoffActive && playTacticalAlert("Arya sensors online. TucsonStreets terminal active.")}>
            <Dog size={20} className="cursor-pointer text-[#d4a843] hover:text-[#d4a843]" />
          </div>
      </div>{/* end left nav pill */}

      {/* ── MAP OVERLAYS ── */}

      {/* Floating Detached Log */}
      {isLogVisible && isLogDetached && (
            <div 
              className={`absolute z-50 transition-opacity duration-300 ${isLogGhost ? 'opacity-65 grayscale pointer-events-none hover:pointer-events-auto' : 'opacity-100'}`}
              style={{ 
                left: logPosition.x, 
                top: logPosition.y, 
                width: logWidth,
                height: '70vh',
                maxHeight: 'calc(100vh - 150px)'
              }}
            >
              <div className="h-full shadow-2xl rounded-lg overflow-hidden border flex flex-col" style={{ borderColor: devSettings.theme.border }}>
                <div 
                  className="cursor-move h-6 flex items-center justify-center shrink-0" 
                  style={{ backgroundColor: devSettings.theme.header }}
                  onMouseDown={handleDragStart}
                >
                  <div className="w-12 h-1 bg-white/20 rounded-full"></div>
                </div>
                <div className="flex-1 overflow-hidden">
                  {isAlarmPanelOpen ? (
                    <AlarmSettingsPanel onClose={() => setIsAlarmPanelOpen(false)} dangerRadius={dangerRadius} setDangerRadius={setDangerRadius} />
                  ) : activeTab === 'game' ? (
                    canUseGameMode ? (
                    <GamePanel
                      incidents={incidents}
                      userGeminiKey={userGeminiKey}
                      onClose={() => setActiveTab('threats')}
                      ghostProtocol={ghostProtocol}
                    />
                    ) : (
                    <div className="flex flex-col items-center justify-center h-full gap-4 text-center p-6">
                      <div className="text-4xl">🎮</div>
                      <h3 className="font-orbitron text-lg text-purple-400">GHOST PROTOCOL REQUIRED</h3>
                      <p className="text-amber-700 text-sm font-mono max-w-xs">Game mode, leaderboard, and tactical quests require GHOST clearance ($24.99/mo)</p>
                      <button onClick={() => setIsSubModalOpen(true)} className="bg-purple-600 hover:bg-purple-500 text-white font-bold py-3 px-6 rounded-xl font-mono uppercase tracking-widest text-sm">UPGRADE TO GHOST</button>
                    </div>
                    )
                  ) : (
                    <IncidentLog
                      incidents={incidents}
                      onSelect={handleIncidentSelect}
                      onSummarize={handleSummarize}
                      summarizingIncidentId={summarizingIncidentId}
                      globalBackoffActive={backoffActive}
                      onClose={() => { setIsLogVisible(false); setIsLogGhost(false); }}
                      selectedIncident={selectedIncident}
                      vicinityReport={vicinityReport}
                      theme={devSettings.theme}
                      isDetached={true}
                      isGhost={isLogGhost}
                      logGhostOpacity={logGhostOpacity}
                      onToggleDetach={() => setIsLogDetached(false)}
                      onToggleGhost={() => setIsLogGhost(!isLogGhost)}
                      onSetGhostOpacity={setLogGhostOpacity}
                      activeTab={activeTab}
                      onTabChange={setActiveTab}
                      currentUserId={user?.uid || null}
                      userLocation={userLocation ? { latitude: userLocation.lat, longitude: userLocation.lng } : null}
                      highlightedIncidentId={highlightedIncidentId}
                      onVenueClick={(lat, lng, name) => setVenueTarget({ lat, lng, name })}
                    />
                  )}
                </div>
              </div>
            </div>
          )}

      {/* Stream status badge */}
      <div className="absolute top-16 right-4 z-40 bg-black/60 backdrop-blur-md border border-white/10 px-4 py-2 rounded-lg flex items-center gap-3">
        <div className={`w-2 h-2 rounded-full ${isScanning ? 'animate-pulse' : ''}`} style={{ backgroundColor: isScanning ? devSettings.theme.primary : '#22c55e' }}></div>
        <span className="text-[10px] font-mono font-bold tracking-widest text-gray-300 uppercase">{isScanning ? 'Scanning Radius' : 'Stream Ready'}</span>
      </div>

      {manualLocationMode && !userLocation && (
            <div className="absolute top-20 left-1/2 -translate-x-1/2 z-50 pointer-events-none">
              <div className="bg-black/80 backdrop-blur-md border border-[#d4a843] p-4 rounded-xl shadow-2xl text-center max-w-xs pointer-events-auto">
                <Target size={24} className="mx-auto mb-2 text-[#d4a843] animate-pulse" />
                <h3 className="font-orbitron text-xs font-bold mb-1 uppercase tracking-widest text-[#d4a843]">Manual Override Active</h3>
                <p className="text-[10px] text-[#d4a843] mb-0 font-mono">Tap map to set position</p>
              </div>
            </div>
          )}
          
      {/* Concept Image Modal */}
      <AnimatePresence>
            {isVisionModalOpen && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="absolute inset-0 z-[60] flex items-center justify-center p-8 bg-black/80 backdrop-blur-sm"
              >
                <div className="relative max-w-5xl w-full terminal-screen rounded-xl shadow-2xl overflow-hidden flex flex-col">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-[#d4a843]/30 bg-stone-900/50">
                    <div className="flex items-center gap-2">
                      <Eye className="text-[#d4a843]" size={18} />
                      <h2 className="font-orbitron text-sm font-bold text-[#d4a843] tracking-widest uppercase">
                        TucsonStreets Vision Engine: {locationInput || "Tucson"}
                      </h2>
                    </div>
                    <button 
                      onClick={() => {
                        setIsVisionModalOpen(false);
                        setConceptImage(null);
                        setVisionPrompt("");
                      }}
                      className="text-gray-300 hover:text-white transition-colors p-1"
                    >
                      <X size={20} />
                    </button>
                  </div>
                  
                  <div className="p-4 border-b border-[#d4a843]/30 bg-black/50 flex gap-4">
                    <input 
                      type="text" 
                      value={visionPrompt}
                      onChange={(e) => setVisionPrompt(e.target.value)}
                      placeholder="Enter custom prompt additions (e.g., 'a glowing neon cactus', 'hover-horses')"
                      className="flex-1 terminal-input px-3 py-2 rounded-sm text-sm"
                      disabled={isGeneratingConcept}
                    />
                    <button 
                      onClick={handleGenerateConcept}
                      disabled={isGeneratingConcept}
                      className="bg-[#d4a843]/20 hover:bg-[#d4a843]/40 border border-[#d4a843]/50 text-[#d4a843] px-6 py-2 rounded-sm font-orbitron font-bold tracking-wider text-sm transition-colors disabled:opacity-50"
                    >
                      {isGeneratingConcept ? 'GENERATING...' : 'GENERATE'}
                    </button>
                  </div>

                  <div className="relative w-full aspect-video bg-black flex items-center justify-center overflow-hidden">
                    {conceptImage ? (
                      <>
                        <img 
                          src={conceptImage} 
                          alt="Wild West Concept" 
                          className="w-full h-full object-contain"
                        />
                        <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-md px-3 py-1.5 rounded border border-[#d4a843]/30 text-[10px] font-mono text-[#d4a843]/80 flex items-center gap-2">
                          <Dog size={12} className="text-[#d4a843]" />
                          <span>GENERATED BY NANO BANANA & ARYA</span>
                        </div>
                      </>
                    ) : (
                      <div className="text-[#d4a843]/50 font-mono text-sm flex flex-col items-center gap-4">
                        {isGeneratingConcept ? (
                          <>
                            <div className="w-12 h-12 border-4 border-[#d4a843]/20 border-t-amber-500 rounded-full animate-spin"></div>
                            <p className="animate-pulse">SYNTHESIZING VISION...</p>
                          </>
                        ) : (
                          <p>AWAITING INPUT PARAMETERS...</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
      </AnimatePresence>

      {selectedIncident && (
            <div className="absolute top-16 left-16 w-72 bg-black/90 backdrop-blur-lg border border-[#d4a843]/30 p-5 rounded-sm shadow-2xl animate-in slide-in-from-left duration-500 z-50">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                   <div className={`w-2 h-2 rounded-full ${selectedIncident.threatLevel === ThreatLevel.CRITICAL ? 'bg-red-500' : 'bg-amber-500'}`}></div>
                   <h3 className="text-[10px] font-black text-[#d4a843] uppercase tracking-widest font-mono">{selectedIncident.type}</h3>
                </div>
                <button onClick={() => setSelectedIncident(null)} className="text-[#d4a843] hover:text-[#d4a843]">&times;</button>
              </div>
              <p className="text-[11px] text-amber-100/70 leading-relaxed mb-4 font-medium font-mono">
                {selectedIncident.description || <span className="text-amber-100/40 italic">No description available.</span>}
              </p>
              {vicinityReport === "Analyzing neighborhood heat..." ? (
                <div className="text-[9px] text-[#d4a843]/70 font-mono bg-amber-500/5 p-2 rounded-sm border border-[#d4a843]/20 leading-tight flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-[#d4a843] animate-pulse"></span>
                  Scanning vicinity intel…
                </div>
              ) : vicinityReport === "Vicinity intel unavailable." || vicinityReport === "Vicinity analysis unavailable." || vicinityReport === "Error analyzing vicinity." ? (
                <div className="text-[9px] text-amber-100/40 font-mono bg-black/40 p-2 rounded-sm border border-amber-900/30 leading-tight">
                  Vicinity intel unavailable for this incident.
                </div>
              ) : vicinityReport ? (
                <div className="text-[9px] text-[#d4a843] font-mono bg-amber-500/10 p-2 rounded-sm border border-[#d4a843]/20 leading-tight">
                  {vicinityReport}
                </div>
              ) : null}
            </div>
          )}

      {/* ── RIGHT LOG PANEL (non-detached) ── */}
      {isLogVisible && !isLogDetached && (
        <div className="absolute top-14 right-0 bottom-8 z-40 flex" style={{ width: logWidth }}>
          <div
            onMouseDown={handleMouseDown}
            className="w-2 cursor-col-resize shrink-0 hover:bg-[#d4a843]/10 transition-colors"
            style={{ background: 'rgba(4,1,8,0.9)', borderLeft: '1px solid rgba(212,168,67,0.2)' }}
          />
          <div className="flex-1 overflow-hidden flex flex-col" style={{ background: 'rgba(4,1,8,0.88)', backdropFilter: 'blur(12px)' }}>
            {activeTab === 'game' ? (
              canUseGameMode ? <GamePanel incidents={incidents} userGeminiKey={userGeminiKey} onClose={() => setActiveTab('threats')} ghostProtocol={ghostProtocol} /> : <div className="flex flex-col items-center justify-center h-full gap-3 p-6"><div className="text-3xl">🔒</div><p className="text-purple-400 font-orbitron text-sm">GHOST CLEARANCE REQUIRED</p><button onClick={() => setIsSubModalOpen(true)} className="bg-purple-600 text-white py-2 px-5 rounded-lg font-mono text-xs uppercase">Upgrade</button></div>
            ) : (
              <IncidentLog
                incidents={incidents}
                onSelect={handleIncidentSelect}
                onSummarize={handleSummarize}
                summarizingIncidentId={summarizingIncidentId}
                globalBackoffActive={backoffActive}
                onClose={() => { setIsLogVisible(false); setIsLogGhost(false); }}
                selectedIncident={selectedIncident}
                vicinityReport={vicinityReport}
                theme={devSettings.theme}
                isDetached={false}
                isGhost={isLogGhost}
                logGhostOpacity={logGhostOpacity}
                onToggleDetach={() => setIsLogDetached(true)}
                onToggleGhost={() => setIsLogGhost(!isLogGhost)}
                onSetGhostOpacity={setLogGhostOpacity}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                currentUserId={user?.uid || null}
                userLocation={userLocation ? { latitude: userLocation.lat, longitude: userLocation.lng } : null}
                highlightedIncidentId={highlightedIncidentId}
                onVenueClick={(lat, lng, name) => setVenueTarget({ lat, lng, name })}
              />
            )}
          </div>
        </div>
      )}

      {/* ── BOTTOM HUD BAR ── */}
      <div
        className="absolute bottom-0 left-0 right-0 z-40 h-8 px-4 flex items-center justify-between text-[10px] text-[#d4a843] font-mono"
        style={{ background: 'rgba(4,1,8,0.82)', backdropFilter: 'blur(14px)', borderTop: '1px solid rgba(212,168,67,0.2)' }}
      >
        <div className="flex items-center gap-6">
          <div className="flex gap-2 items-center">
            <span className="text-[#00e5ff] font-black tracking-widest">LOC:</span>
            <span
              className="text-[#d4a843] cursor-default select-none"
              onClick={() => {
                ghostClicksRef.current += 1;
                if (ghostClicksRef.current >= 5) { ghostClicksRef.current = 0; setGhostProtocol(p => !p); }
                setTimeout(() => { ghostClicksRef.current = 0; }, 3000);
              }}
            >{userLocation ? `${userLocation.lat.toFixed(6)}, ${userLocation.lng.toFixed(6)}` : 'SIGNAL ACQUIRING'}</span>
            {ghostProtocol && <span className="text-[9px] text-purple-400 ml-1 animate-pulse">👁 GHOST</span>}
          </div>
          <div className="flex gap-2 items-center border-l border-[#d4a843]/20 pl-4">
            <span className="text-[#00e5ff] font-black tracking-widest">PERIMETER:</span>
            <span className={inDangerZone ? 'text-red-500 font-bold' : 'text-[#d4a843]'}>{inDangerZone ? 'BREACHED' : 'SECURE'}</span>
          </div>
        </div>
        <div className="flex items-center gap-4 group relative">
          <span className="text-[9px] opacity-40 uppercase tracking-widest cursor-help text-[#d4a843]" onClick={handleVersionClick}>
            Created, coded, hosted by NobleGlitch
          </span>
          <div className="absolute bottom-full right-0 mb-2 w-48 p-3 bg-black border border-[#d4a843]/30 text-[10px] text-[#d4a843] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none rounded-sm">
            NobleGlitch is a mission-driven architect. Building tools for self-reliance and digital dignity.
          </div>
        </div>

        {/* Subtle, nearly-transparent lock for hidden admin login */}
        {!user && (
          <button
            onClick={async () => {
              try { await signInWithGoogle(); } catch (err) { console.error('[admin-auth-lock]', err); }
            }}
            className="absolute bottom-1 right-1.5 z-50 opacity-[0.07] hover:opacity-100 transition-opacity cursor-pointer text-[#d4a843] bg-transparent border-none p-0 outline-none"
            style={{ width: '10px', height: '11px', pointerEvents: 'auto' }}
            title="Admin Login"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-full h-full">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
